import React from 'react';
import { geoBounds, geoConicConformal, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import 'd3-transition'; // adds selection.transition(), used for animated zoom
import { zoom as d3Zoom, zoomIdentity } from 'd3-zoom';
import { feature } from 'topojson-client';

// ============================================================
// Europe choropleth — d3-geo SVG rendered from the local TopoJSON
// (Natural Earth 1:50m via world-atlas). No raster tiles: a conic conformal
// projection fit to Europe, refit on resize, with d3-zoom for zoom and pan.
// ============================================================

const { useState, useEffect, useRef, useMemo } = React;

// TopoJSON `properties.name` → the country key used in orgs.json / filters.
export const NAME_TO_CSVKEY = {
  "Albania": "Albania", "Austria": "Austria", "Belgium": "Belgium",
  "Bosnia and Herz.": "Bosnia & Herzegovina", "Bosnia and Herzegovina": "Bosnia & Herzegovina",
  "Bulgaria": "Bulgaria", "Croatia": "Croatia", "Cyprus": "Cyprus",
  "Czechia": "Czechia", "Czech Rep.": "Czechia", "Czech Republic": "Czechia",
  "Denmark": "Denmark", "Estonia": "Estonia", "Finland": "Finland",
  "France": "France", "Germany": "Germany", "Greece": "Greece",
  "Hungary": "Hungary", "Iceland": "Iceland", "Ireland": "Ireland",
  "Italy": "Italy", "Kosovo": "Kosovo", "Latvia": "Latvia",
  "Lithuania": "Lithuania", "Luxembourg": "Luxembourg", "Malta": "Malta",
  "Moldova": "Moldova", "Montenegro": "Montenegro", "Netherlands": "Netherlands",
  "Macedonia": "North Macedonia", "North Macedonia": "North Macedonia",
  "Norway": "Norway", "Poland": "Poland", "Portugal": "Portugal",
  "Romania": "Romania", "Serbia": "Serbia", "Slovakia": "Slovakia",
  "Slovenia": "Slovenia", "Spain": "Spain", "Sweden": "Sweden",
  "Switzerland": "Switzerland", "United Kingdom": "United Kingdom",
  "Ukraine": "Ukraine", "Turkey": "Turkey",
};

// Geographic window the projection is fit to: Iceland to eastern Ukraine /
// Turkey, North Cape to Cyprus. A lon/lat box rather than the country
// geometries themselves, because Natural Earth folds overseas territories
// (French Guiana, the Azores, Bouvet Island…) into their parent country.
// The ring is clockwise (d3 treats spherical polygons as oriented; the other
// winding would mean "the whole globe except this box") and densified so the
// edges follow the parallels instead of bulging along great circles.
const FIT_BOX = (() => {
  const [west, east, south, north] = [-24.5, 45, 33.5, 71.5];
  const ring = [];
  for (let lon = west; lon < east; lon += 1) ring.push([lon, north]);
  for (let lat = north; lat > south; lat -= 1) ring.push([east, lat]);
  for (let lon = east; lon > west; lon -= 1) ring.push([lon, south]);
  for (let lat = south; lat < north; lat += 1) ring.push([west, lat]);
  ring.push(ring[0]);
  return { type: 'Polygon', coordinates: [ring] };
})();
const FIT_PADDING = 6;

// Only land whose bounding box touches this wider window is drawn. It keeps the
// far side of the globe out of the conic projection and skips work on shapes
// that the clip extent would discard anyway.
const DRAW_WINDOW = { west: -45, east: 80, south: 15, north: 85 };
const inDrawWindow = (f) => {
  const [[w, s], [e, n]] = geoBounds(f);
  if (n < DRAW_WINDOW.south || s > DRAW_WINDOW.north) return false;
  if (w > e) return true; // crosses the antimeridian (Russia, USA): let the clip sort it out
  return e >= DRAW_WINDOW.west && w <= DRAW_WINDOW.east;
};

// Discrete sequential ramp — light cream → gold → deep clay. Fixed breakpoints
// give clearly distinguishable colours between e.g. Lithuania (5) and Italy (30).
// Must stay in sync with the legend swatches in views.jsx.
const RAMP = [
  { min: 1,  color: '#F3E2B3' },  // 1–2
  { min: 3,  color: '#EDC873' },  // 3–5
  { min: 6,  color: '#E0A53C' },  // 6–10
  { min: 11, color: '#CE7A2A' },  // 11–20
  { min: 21, color: '#B0531F' },  // 21–40
  { min: 41, color: '#7E2E12' },  // 41+
];
const colorFor = (c) => {
  let col = RAMP[0].color;
  for (const stop of RAMP) { if (c >= stop.min) col = stop.color; }
  return col;
};

// Country labels: drawn at the centroid of each shaded country's largest
// polygon, only when the label fits inside that polygon's projected bounding
// box (with a small tolerance). Width is estimated from the mono font metrics.
const LABEL_FONT_PX = 9;
const LABEL_CHAR_W = 0.66;   // em per uppercase mono glyph incl. letter-spacing
const LABEL_SLACK = 4;       // px the label may overhang the bounding box
const LABEL_MIN_H = 12;
const LABEL_ALIASES = { 'United Kingdom': 'UK', 'Bosnia and Herz.': 'Bosnia & Herz.', 'Macedonia': 'N. Macedonia' };
const NARROW_BREAKPOINT = 640; // container px below which labels are hidden

// Zoom / pan
const ZOOM_EXTENT = [1, 8];
const ZOOM_STEP = 1.5;        // +/- buttons
const DBLTAP_ZOOM = 2;        // double-click / double-tap
const TAP_MAX_MS = 300, TAP_MAX_PX = 24, TAP_MOVE_PX = 10;

const largestPolygon = (f, path) => {
  if (f.geometry.type !== 'MultiPolygon') return f.geometry;
  let best = null, bestArea = -1;
  for (const coordinates of f.geometry.coordinates) {
    const poly = { type: 'Polygon', coordinates };
    const a = path.area(poly);
    if (a > bestArea) { bestArea = a; best = poly; }
  }
  return best;
};

// Choropleth strokes (unchanged from the tile-map version).
const STROKE_DEFAULT = 'rgba(60,50,40,0.55)';
const STROKE_STRONG = '#1A1A1A';

export function EuropeChoroplethMap({ counts, maxCount, selectedCountries, onToggleCountry, activeColor }) {
  const sel = selectedCountries || new Set();
  const containerRef = useRef(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [world, setWorld] = useState(null);   // GeoJSON features (all countries)
  const [err, setErr] = useState(false);
  const [hover, setHover] = useState(null);   // { name, count, x, y }

  // Zoom state: the live transform is applied straight to the <g> for smooth
  // gestures; only the scale is mirrored into React state (rAF-throttled) for
  // label counter-scaling, touch-action and the control buttons.
  const svgRef = useRef(null);
  const gRef = useRef(null);
  const zoomRef = useRef(null);
  const kRef = useRef(1);
  const rafRef = useRef(null);
  const [zoomK, setZoomK] = useState(1);
  const touchRef = useRef({ start: null, maxTouches: 0, lastTap: null, lastEnd: 0 });

  // Track the container size so the projection can be refit on resize.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      const w = Math.round(r.width), h = Math.round(r.height);
      if (w > 0 && h > 0) setSize(s => (s.w === w && s.h === h) ? s : { w, h });
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Load the TopoJSON once and convert to GeoJSON.
  useEffect(() => {
    let cancelled = false;
    fetch((window.__resources && window.__resources.countriesTopo) || '/data/countries-50m.json')
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(topo => { if (!cancelled) setWorld(feature(topo, topo.objects.countries).features); })
      .catch(() => { if (!cancelled) setErr(true); });
    return () => { cancelled = true; };
  }, []);

  const applyTransform = (t) => {
    kRef.current = t.k;
    if (gRef.current) gRef.current.setAttribute('transform', t.toString());
    if (rafRef.current == null) {
      rafRef.current = requestAnimationFrame(() => { rafRef.current = null; setZoomK(kRef.current); });
    }
  };
  useEffect(() => () => { if (rafRef.current != null) cancelAnimationFrame(rafRef.current); }, []);

  // Attach d3-zoom to the SVG. Re-created whenever the projection is refit so
  // the extents match; each refit starts from the fitted view.
  useEffect(() => {
    const svgEl = svgRef.current;
    if (!svgEl || !size.w || !world) return;
    const { w, h } = size;
    const svg = select(svgEl);
    const zoom = d3Zoom()
      .scaleExtent(ZOOM_EXTENT)
      .extent([[0, 0], [w, h]])
      .translateExtent([[0, 0], [w, h]])   // Europe can't be dragged off-screen
      .clickDistance(4)                     // a click that jitters a few px is still a click, not a drag
      // d3's default multiplies Ctrl-wheel deltas by 10 so trackpad pinches feel
      // right, but that turns one mouse-wheel notch into a 4x jump. Keep the
      // pinch feel and cap a single event at ~1.4x.
      .wheelDelta((event) => {
        const d = -event.deltaY * (event.deltaMode === 1 ? 0.05 : event.deltaMode ? 1 : 0.002) * 10;
        return Math.max(-0.5, Math.min(0.5, d));
      })
      .filter((event) => {
        // Plain wheel scrolls the page; Ctrl/Cmd + wheel (and trackpad pinch) zooms.
        if (event.type === 'wheel') return event.ctrlKey || event.metaKey;
        // At scale 1 a single finger belongs to page scrolling (touch-action: pan-y);
        // pinch always zooms, and one-finger pan is enabled once zoomed in.
        if (event.type === 'touchstart') return event.touches.length > 1 || kRef.current > 1;
        return !event.ctrlKey && !event.button;   // primary mouse button only
      })
      .on('zoom', (event) => applyTransform(event.transform));
    svg.call(zoom).on('dblclick.zoom', null);     // double-click/tap handled below
    zoomRef.current = zoom;
    svg.call(zoom.transform, zoomIdentity);
    return () => { svg.on('.zoom', null); zoomRef.current = null; };
  }, [size, world]);

  const zoomBy = (factor, point) => {
    const zoom = zoomRef.current;
    if (!zoom || !svgRef.current) return;
    select(svgRef.current).transition().duration(220).call(zoom.scaleBy, factor, point);
  };
  const resetZoom = () => {
    const zoom = zoomRef.current;
    if (!zoom || !svgRef.current) return;
    select(svgRef.current).transition().duration(320).call(zoom.transform, zoomIdentity);
  };

  // Double-tap to zoom (d3-zoom's own tap detection is bypassed by the touch
  // filter at scale 1). Listens in the capture phase because d3 stops
  // propagation of touch events it handles.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const T = touchRef.current;
    const onStart = (e) => {
      if (e.touches.length === 1) {
        const t = e.touches[0];
        T.start = { x: t.clientX, y: t.clientY };
        T.maxTouches = 1;
      } else {
        T.maxTouches = Math.max(T.maxTouches, e.touches.length);
      }
    };
    const onEnd = (e) => {
      T.lastEnd = Date.now();
      if (e.touches.length !== 0 || e.changedTouches.length !== 1 || T.maxTouches !== 1 || !T.start) { T.lastTap = null; return; }
      const t = e.changedTouches[0];
      if (Math.hypot(t.clientX - T.start.x, t.clientY - T.start.y) > TAP_MOVE_PX) { T.lastTap = null; return; }
      const r = el.getBoundingClientRect();
      const p = [t.clientX - r.left, t.clientY - r.top];
      const now = Date.now();
      if (T.lastTap && now - T.lastTap.t < TAP_MAX_MS && Math.hypot(p[0] - T.lastTap.p[0], p[1] - T.lastTap.p[1]) < TAP_MAX_PX) {
        T.lastTap = null;
        zoomBy(DBLTAP_ZOOM, p);
      } else {
        T.lastTap = { t: now, p };
      }
    };
    el.addEventListener('touchstart', onStart, { capture: true, passive: true });
    el.addEventListener('touchend', onEnd, { capture: true, passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart, { capture: true });
      el.removeEventListener('touchend', onEnd, { capture: true });
    };
  }, []);

  const onDoubleClick = (e) => {
    if (Date.now() - touchRef.current.lastEnd < 500) return;   // synthesized after a double-tap
    const p = pointerPos(e);
    zoomBy(DBLTAP_ZOOM, [p.x, p.y]);
  };

  // Projection + path generator, refit whenever the container size changes.
  const { path } = useMemo(() => {
    const { w, h } = size;
    if (!w || !h) return { path: null };
    const projection = geoConicConformal()
      .parallels([40, 60])
      .rotate([-10, 0])
      .fitExtent([[FIT_PADDING, FIT_PADDING], [w - FIT_PADDING, h - FIT_PADDING]], FIT_BOX)
      .clipExtent([[0, 0], [w, h]]);
    return { path: geoPath(projection) };
  }, [size]);

  // Pre-render path strings once per size: neutral land (every other country
  // in view) and the choropleth countries.
  const { land, shapes } = useMemo(() => {
    if (!world || !path) return { land: [], shapes: [] };
    const land = [], shapes = [];
    for (const f of world) {
      const name = f.properties.name;
      const csvKey = NAME_TO_CSVKEY[name];
      if (!csvKey && !inDrawWindow(f)) continue;
      const d = path(f);
      if (!d) continue;
      if (!csvKey) { land.push({ name, d }); continue; }
      const main = largestPolygon(f, path);
      const [[x0, y0], [x1, y1]] = path.bounds(main);
      const label = (LABEL_ALIASES[name] || name).toUpperCase();
      const labelW = label.length * LABEL_FONT_PX * LABEL_CHAR_W;
      shapes.push({ name, csvKey, d, label, labelW, boxW: x1 - x0, boxH: y1 - y0, anchor: path.centroid(main) });
    }
    return { land, shapes };
  }, [world, path]);

  const pointerPos = (e) => {
    const r = containerRef.current.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  if (err) {
    return <div ref={containerRef} className="europe-choropleth-map"><div className="map-err">Couldn't load the map.</div></div>;
  }

  const hovered = hover ? shapes.find(s => s.name === hover.name) : null;

  return (
    <div ref={containerRef} className={`europe-choropleth-map${size.w && size.w < NARROW_BREAKPOINT ? ' is-narrow' : ''}`}>
      {!world && <div className="map-loading">Loading map…</div>}
      {world && size.w > 0 && (
        <svg ref={svgRef} viewBox={`0 0 ${size.w} ${size.h}`} width="100%" height="100%" role="img"
             aria-label="Map of Europe shaded by number of organisations per country"
             onDoubleClick={onDoubleClick}
             style={{ touchAction: zoomK > 1 ? 'none' : 'pan-y', cursor: zoomK > 1 ? 'grab' : 'default' }}>
          <rect className="map-sea" x="0" y="0" width={size.w} height={size.h} />

          <g ref={gRef}>
          {/* Neutral land: neighbouring countries outside the directory */}
          {land.map(l => <path key={l.name} d={l.d} className="map-land" vectorEffect="non-scaling-stroke" />)}

          {/* Choropleth: countries in the directory */}
          {shapes.map(s => {
            const c = counts[s.csvKey] || 0;
            const isSelected = sel.has(s.csvKey);
            const isDimmed = sel.size > 0 && !isSelected;
            return (
              <path key={s.name} d={s.d} data-name={s.name}
                    className={c > 0 ? 'map-country' : 'map-country map-land'}
                    fill={c > 0 ? colorFor(c) : undefined}
                    fillOpacity={c > 0 ? (isDimmed ? 0.35 : 0.92) : undefined}
                    stroke={isSelected ? STROKE_STRONG : (c > 0 ? STROKE_DEFAULT : undefined)}
                    strokeWidth={isSelected ? 2 : (c > 0 ? 0.7 : undefined)}
                    vectorEffect="non-scaling-stroke"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) => setHover({ name: s.name, count: c, ...pointerPos(e) })}
                    onMouseMove={(e) => setHover(h => h ? { ...h, ...pointerPos(e) } : h)}
                    onMouseLeave={() => setHover(null)}
                    onClick={() => { if (c > 0) onToggleCountry(s.csvKey); }} />
            );
          })}

          {/* Hover outline drawn last so it sits above neighbouring borders */}
          {hovered && (
            <path d={hovered.d} fill="none" stroke={STROKE_STRONG} strokeWidth={2.4} vectorEffect="non-scaling-stroke" pointerEvents="none" />
          )}

          {/* Labels: shaded countries only, where the name fits at the current zoom
              (more appear as you zoom in); counter-scaled so they keep their size;
              hidden on narrow containers via CSS */}
          <g className="map-labels" aria-hidden="true">
            {shapes.map(s => {
              const c = counts[s.csvKey] || 0;
              if (c === 0) return null;
              const fits = s.labelW <= s.boxW * zoomK + LABEL_SLACK && s.boxH * zoomK >= LABEL_MIN_H;
              if (!fits) return null;
              const isDimmed = sel.size > 0 && !sel.has(s.csvKey);
              return (
                <text key={s.name} className="map-country-label"
                      transform={`translate(${s.anchor[0]} ${s.anchor[1]}) scale(${1 / zoomK})`}
                      textAnchor="middle" dominantBaseline="central" opacity={isDimmed ? 0.45 : 1}>
                  {s.label}
                </text>
              );
            })}
          </g>
          </g>
        </svg>
      )}
      {world && size.w > 0 && (
        <div className="map-zoom-controls">
          <button type="button" onClick={() => zoomBy(ZOOM_STEP)} disabled={zoomK >= ZOOM_EXTENT[1] - 0.001} title="Zoom in" aria-label="Zoom in">＋</button>
          <button type="button" onClick={() => zoomBy(1 / ZOOM_STEP)} disabled={zoomK <= 1.001} title="Zoom out" aria-label="Zoom out">−</button>
          <button type="button" onClick={resetZoom} disabled={zoomK <= 1.001} title="Reset view" aria-label="Reset view">
            <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true"><path d="M8 2v4M8 14v-4M2 8h4M14 8h-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" fill="none"/><circle cx="8" cy="8" r="1.4" fill="currentColor"/></svg>
          </button>
        </div>
      )}
      {hover && (
        <div className="map-tooltip" style={{ left: hover.x + 14, top: hover.y + 14 }}>
          <div className="map-tooltip-name">{hover.name}</div>
          <div className="map-tooltip-count">
            {hover.count > 0 ? `${hover.count} organisation${hover.count === 1 ? '' : 's'}` : 'No matches'}
          </div>
          {hover.count > 0 && <div className="map-tooltip-hint">Click to filter</div>}
        </div>
      )}
    </div>
  );
}
